import React, { useState } from 'react';
import { 
  Share2, Copy, Check, X, Twitter, Linkedin, 
  MessageCircle, Mail, Link2, Sparkles, Trophy,
  Users, ChevronRight
} from 'lucide-react';

const ShareModal = ({ 
  isOpen, 
  onClose, 
  shareData,
  type = 'progress' // 'progress', 'completion', 'invite', 'achievement'
}) => {
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  if (!isOpen) return null;

  const {
    challengeName = '7-Day Challenge',
    dayNumber = 1,
    dayTitle = '',
    theme = '',
    totalDays = 7,
    completedDays = 0,
    userName = 'Someone'
  } = shareData || {};

  // Generate share messages based on type
  const getShareContent = () => {
    // Live app URL — no hardcoded dead domains. Frontend already has
    // REACT_APP_BACKEND_URL wired at build time.
    const appUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    
    switch (type) {
      case 'completion':
        return {
          title: `Day ${dayNumber} Complete!`,
          text: `Just completed Day ${dayNumber} of the ${challengeName}! 🔥\n\n"${theme}"\n\nBuilding new habits one day at a time.\n\n`,
          hashtags: '#ReclaimApp #PersonalGrowth #Mindset',
          url: appUrl
        };
      case 'achievement':
        return {
          title: `Challenge Complete!`,
          text: `🏆 I just completed the ${challengeName}!\n\n${totalDays} days of transformation. Feeling stronger, clearer, and more aligned.\n\n`,
          hashtags: '#ReclaimApp #ChallengeComplete #Transformation',
          url: appUrl
        };
      case 'invite':
        return {
          title: `Join me on Reclaim`,
          text: `I'm doing the ${challengeName} on Reclaim - a powerful app for mindset and personal growth.\n\nWant to join me? 👇\n\n`,
          hashtags: '#ReclaimApp #PersonalGrowth',
          url: appUrl
        };
      default: // progress
        return {
          title: `${completedDays}/${totalDays} Days`,
          text: `Day ${dayNumber} of the ${challengeName} ✅\n\nToday's focus: "${theme}"\n\n${completedDays}/${totalDays} days complete. Showing up daily.\n\n`,
          hashtags: '#ReclaimApp #DailyPractice #Growth',
          url: appUrl
        };
    }
  };

  const content = getShareContent();
  const fullText = content.text + content.hashtags + '\n' + content.url;

  // Share handlers
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareToTwitter = () => {
    const tweetText = encodeURIComponent(content.text + content.hashtags);
    const url = encodeURIComponent(content.url);
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=${url}`, '_blank');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(content.url);
    const text = encodeURIComponent(content.text);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareToWhatsApp = () => {
    const text = encodeURIComponent(fullText);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(content.title);
    const body = encodeURIComponent(fullText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: content.title,
          text: content.text,
          url: content.url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  // Get icon and color based on type
  const getTypeStyle = () => {
    switch (type) {
      case 'completion':
        return { icon: Check, color: 'from-emerald-500 to-green-500', label: 'Share Completion' };
      case 'achievement':
        return { icon: Trophy, color: 'from-amber-500 to-yellow-500', label: 'Share Achievement' };
      case 'invite':
        return { icon: Users, color: 'from-blue-500 to-cyan-500', label: 'Invite Friends' };
      default:
        return { icon: Share2, color: 'from-purple-500 to-pink-500', label: 'Share Progress' };
    }
  };

  const typeStyle = getTypeStyle();
  const TypeIcon = typeStyle.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center">
      <div className="w-full max-w-md bg-slate-900 rounded-t-3xl border-t border-white/10 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${typeStyle.color} 
                          flex items-center justify-center`}>
              <TypeIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{typeStyle.label}</h3>
              <p className="text-xs text-slate-400">{challengeName}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Preview Card */}
        <div className="p-6">
          <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-2xl p-5 
                        border border-indigo-500/30 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-indigo-400 uppercase tracking-wider">Preview</span>
            </div>
            <p className="text-white/90 text-sm whitespace-pre-line leading-relaxed">
              {content.text}
            </p>
            <p className="text-indigo-400 text-sm mt-2">{content.hashtags}</p>
          </div>

          {/* Share Options */}
          <div className="space-y-3">
            {/* Native Share (Mobile) */}
            {navigator.share && (
              <button
                onClick={nativeShare}
                className="w-full p-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 
                         text-white font-medium flex items-center justify-center gap-2
                         hover:opacity-90 transition-opacity"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
            )}

            {/* Social Platforms */}
            <div className="grid grid-cols-4 gap-3">
              <button
                onClick={shareToTwitter}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 
                         hover:bg-white/10 transition-colors"
                data-testid="share-twitter"
              >
                <div className="w-10 h-10 rounded-full bg-[#1DA1F2]/20 flex items-center justify-center">
                  <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                </div>
                <span className="text-xs text-slate-400">Twitter</span>
              </button>

              <button
                onClick={shareToLinkedIn}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 
                         hover:bg-white/10 transition-colors"
                data-testid="share-linkedin"
              >
                <div className="w-10 h-10 rounded-full bg-[#0A66C2]/20 flex items-center justify-center">
                  <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                </div>
                <span className="text-xs text-slate-400">LinkedIn</span>
              </button>

              <button
                onClick={shareToWhatsApp}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 
                         hover:bg-white/10 transition-colors"
                data-testid="share-whatsapp"
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                </div>
                <span className="text-xs text-slate-400">WhatsApp</span>
              </button>

              <button
                onClick={shareViaEmail}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 
                         hover:bg-white/10 transition-colors"
                data-testid="share-email"
              >
                <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-400" />
                </div>
                <span className="text-xs text-slate-400">Email</span>
              </button>
            </div>

            {/* Copy Link */}
            <button
              onClick={copyToClipboard}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10
                       flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
              data-testid="copy-share-link"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 text-slate-400" />
                  <span className="text-white">Copy to Clipboard</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-8 pt-2">
          <p className="text-xs text-slate-500 text-center">
            Share your journey and inspire others to transform
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Quick share button component
export const ShareButton = ({ 
  onClick, 
  variant = 'default', // 'default', 'icon', 'full'
  label = 'Share',
  className = ''
}) => {
  if (variant === 'icon') {
    return (
      <button
        onClick={onClick}
        className={`p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${className}`}
        data-testid="share-btn"
      >
        <Share2 className="w-5 h-5 text-white" />
      </button>
    );
  }

  if (variant === 'full') {
    return (
      <button
        onClick={onClick}
        className={`w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 
                   text-white font-medium flex items-center justify-center gap-2
                   hover:opacity-90 transition-opacity ${className}`}
        data-testid="share-btn"
      >
        <Share2 className="w-5 h-5" />
        {label}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 
                 transition-colors flex items-center gap-2 ${className}`}
      data-testid="share-btn"
    >
      <Share2 className="w-4 h-4 text-white" />
      <span className="text-sm text-white">{label}</span>
    </button>
  );
};

export default ShareModal;
